import { Fragment, useMemo } from "react";
import { Checkbox, Text } from "@mantine/core";

import { useSelector } from "../../../redux/hooks";
import { Flex, StyledTable, StyledTh, StyledTr } from "../../../styled";
import {
  getSectionColumnKeys,
  NMEAPreviewNMEARow,
  isRowLockedFromUnselect as isRowLockedFromUnselectHelper,
} from "./NMEAPreviewShared";
import NMEATimeSearchInput from "./NMEATimeSearchInput";
import {
  getVisibleNMEARowsForQuery,
  sectionHasNMEATimeRows,
  type INMEARow,
  type INMEASection,
  sortNMEARowsByTime,
} from "./NMEAAutomationModal.utils";
import { useNMEASectionSort } from "./useNMEASectionSort";
import { useNMEATimeSearch } from "./useNMEATimeSearch";

interface Props {
  section: INMEASection;
  sectionIndex: number;
  sectionName?: string | null;
  selectedSections: number[][];
  setSelectedSections: (selectedSections: number[][]) => void;
  onlyOneAllowed: boolean;
}

const NMEAAutomationModalPreview = ({
  section,
  sectionIndex,
  sectionName,
  selectedSections,
  setSelectedSections,
  onlyOneAllowed,
}: Props) => {
  const { lbentry } = useSelector(state => state.generatorSlice);

  const isRowLockedFromUnselect = (row: INMEARow) =>
    isRowLockedFromUnselectHelper(lbentry, section, row);

  const hasTimeRows = sectionHasNMEATimeRows(section.rows);
  const {
    timeQuery,
    debouncedTimeQuery,
    handleTimeQueryChange,
    clearTimeQuery,
  } = useNMEATimeSearch({
    enabled: hasTimeRows,
  });
  const { sortAscending, handleToggleSectionSort } = useNMEASectionSort({
    sectionIndex,
    rows: section.rows,
    setSelectedSections,
  });
  const displayRows = hasTimeRows
    ? sortNMEARowsByTime(section.rows, sortAscending)
    : [...section.rows];
  const visibleRows = hasTimeRows
    ? getVisibleNMEARowsForQuery(displayRows, debouncedTimeQuery)
    : displayRows.map((row, sortedIndex) => ({ row, sortedIndex }));
  const sectionColumnKeys = getSectionColumnKeys(displayRows);
  const selectedIndices = selectedSections[sectionIndex] ?? [];

  const { allSelected, someSelected } = useMemo(() => {
    if (!visibleRows.length) {
      return { allSelected: false, someSelected: false };
    }

    const targetRows = visibleRows
      .map(({ row, sortedIndex }) => ({
        sortedIndex,
        isSelected: selectedIndices.includes(sortedIndex),
        isLocked: isRowLockedFromUnselect(row),
      }))
      .filter(row => !row.isLocked);

    const selectedCount = targetRows.filter(row => row.isSelected).length;
    return {
      allSelected: targetRows.length > 0 && selectedCount === targetRows.length,
      someSelected: selectedCount > 0,
    };
  }, [visibleRows, selectedSections, lbentry?.NMEAData]);

  const handleToggle = (
    prev: boolean,
    sortedIndex: number,
    checked: boolean,
  ) => {
    const row = displayRows[sortedIndex];
    const isLocked = row ? prev && isRowLockedFromUnselect(row) : false;

    if (isLocked) return;

    const updatedSections = [...selectedSections];

    if (!checked) {
      updatedSections[sectionIndex] = (
        updatedSections[sectionIndex] ?? []
      ).filter(i => i !== sortedIndex);
    } else {
      if (!updatedSections[sectionIndex]) {
        updatedSections[sectionIndex] = [];
      }
      if (onlyOneAllowed) {
        updatedSections[sectionIndex] = [];
      }
      updatedSections[sectionIndex].push(sortedIndex);
    }
    setSelectedSections(updatedSections);
  };

  const handleToggleSection = (checked: boolean) => {
    const updatedSections = [...selectedSections];

    if (!checked) {
      const visibleIndices = new Set(
        visibleRows.map(({ sortedIndex }) => sortedIndex),
      );
      updatedSections[sectionIndex] = selectedIndices.filter(index => {
        if (!visibleIndices.has(index)) return true;
        const row = displayRows[index];
        return Boolean(row && isRowLockedFromUnselect(row));
      });
    } else if (onlyOneAllowed) {
      updatedSections[sectionIndex] =
        visibleRows.length > 0 ? [visibleRows[0].sortedIndex] : [];
    } else {
      updatedSections[sectionIndex] = Array.from(
        new Set([
          ...(updatedSections[sectionIndex] ?? []),
          ...visibleRows.map(({ sortedIndex }) => sortedIndex),
        ]),
      ).sort((a, b) => a - b);
    }

    setSelectedSections(updatedSections);
  };

  return (
    <Flex direction="column" style={{ width: "100%" }}>
      <Flex
        display="flex"
        direction="row"
        className="sticky-section-header"
        justify="flex-end"
        align="center"
        gap={8}>
        {sectionName && (
          <Text
            fz={13}
            c="var(--mantine-color-dimmed)"
            tt="uppercase"
            style={{
              flex: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
            {sectionName}
          </Text>
        )}
        {hasTimeRows && (
          <NMEATimeSearchInput
            value={timeQuery}
            onChange={handleTimeQueryChange}
            onClear={clearTimeQuery}
            sortAscending={sortAscending}
            onToggleSort={handleToggleSectionSort}
          />
        )}
      </Flex>
      <StyledTable style={{ border: "none" }}>
        <StyledTr
          style={{ position: "sticky", top: hasTimeRows ? 36 : 0, zIndex: 1 }}>
          {sectionColumnKeys.map(([nmeaKey, nmeaCol]) => (
            <StyledTh
              key={`header-${nmeaKey}`}
              style={{ whiteSpace: "nowrap" }}>
              {nmeaCol.name?.replaceAll("<br>", " ") ?? nmeaKey}
            </StyledTh>
          ))}
          <StyledTh>
            <Flex justify="center" align="center" w="100%" h="100%" px={4}>
              <Checkbox
                size="sm"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={event =>
                  handleToggleSection(event.currentTarget.checked)
                }
              />
            </Flex>
          </StyledTh>
        </StyledTr>

        {visibleRows.map(({ row, sortedIndex }) => {
          const isSelected = selectedIndices.includes(sortedIndex);
          const isLocked = isSelected && isRowLockedFromUnselect(row);

          return (
            <Fragment key={`${row.NMEAId}-${sortedIndex}`}>
              <NMEAPreviewNMEARow
                nmeaRow={row}
                columnKeys={sectionColumnKeys}
                navigationId={`${sectionIndex}-${sortedIndex}`}
                selected={isSelected}
                disabled={isLocked}
                onToggle={checked =>
                  handleToggle(isSelected, sortedIndex, checked)
                }
              />
            </Fragment>
          );
        })}
      </StyledTable>
    </Flex>
  );
};

export default NMEAAutomationModalPreview;
